/**
 * SurveysList Component
 * Extracted from AdminDashboard — manages the survey table, search/filter,
 * pagination, create form, response viewer, and report viewer.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ResponseViewer from './ResponseViewer';
import ReportViewer from './ReportViewer';
import { API_BASE_URL } from '../services/api';
import '../styles/design-system.css';
import {
    Plus, Search, Eye, Pencil, FileText, Mail, Link2, Check,
    Lock, Unlock, Trash2, ChevronLeft, ChevronRight, ChevronsLeft,
    ChevronsRight, AlertCircle, Briefcase, X, FileUp, Type, MessageSquare
} from 'lucide-react';

function SurveysList({ adminUser, onLogout }) {
    /* ── State ── */
    const [surveys, setSurveys] = useState([]);
    const [stats, setStats] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({ page: 1, pageSize: 25, total: 0, totalPages: 1 });

    // Filtering & search
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filter, setFilter] = useState({ status: '', department: '' });
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    // Modals & actions
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [viewingResponses, setViewingResponses] = useState(null);
    const [viewingReport, setViewingReport] = useState(null);

    // Create form
    const [newSurvey, setNewSurvey] = useState({
        employee_name: '', employee_email: '', employee_id: '',
        role_title: '', department: '', job_description: '',
        created_by: adminUser?.username || 'People Operations',
        send_email: true,
    });
    const [jdInputMode, setJdInputMode] = useState('text');
    const [jdFile, setJdFile] = useState(null);
    const [jdFileError, setJdFileError] = useState('');

    // Edit modal
    const [editingSurvey, setEditingSurvey] = useState(null);
    const [editForm, setEditForm] = useState({ employee_name: '', employee_email: '', employee_id: '', role_title: '', department: '' });
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');

    // Action indicators
    const [copiedLink, setCopiedLink] = useState(null);
    const [sendingEmail, setSendingEmail] = useState(null);
    const [togglingClosed, setTogglingClosed] = useState(null);
    const [sendingPulse, setSendingPulse] = useState(null);

    /* ── Auth helper ── */
    const getAuthHeaders = () => {
        const token = localStorage.getItem('adminToken');
        const swaPrincipal = localStorage.getItem('swaPrincipal');
        return {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
        };
    };

    /* ── Debounce search ── */
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setPagination(p => ({ ...p, page: 1 })); }, 350);
        return () => clearTimeout(t);
    }, [searchTerm]);

    /* ── Load data ── */
    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams();
            if (filter.status) params.append('status', filter.status);
            if (filter.department) params.append('department', filter.department);
            if (debouncedSearch) params.append('search', debouncedSearch);
            params.append('page', pagination.page);
            params.append('page_size', pagination.pageSize);
            params.append('sort_by', sortBy);
            params.append('sort_order', sortOrder);

            const headers = getAuthHeaders();

            const [surveysRes, statsRes, deptsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/admin/sessions?${params}`, { headers }).then(r => {
                    if (r.status === 401) throw new Error('Session expired');
                    if (!r.ok) throw new Error(`API error: ${r.status}`);
                    return r.json();
                }),
                fetch(`${API_BASE_URL}/api/admin/stats`, { headers }).then(r => {
                    if (!r.ok) throw new Error(`Stats error`);
                    return r.json();
                }),
                fetch(`${API_BASE_URL}/api/admin/departments`, { headers }).then(r => {
                    if (!r.ok) throw new Error(`Depts error`);
                    return r.json();
                }),
            ]);

            setSurveys(surveysRes.items || surveysRes);
            setPagination(p => ({ ...p, total: surveysRes.total || surveysRes.length, totalPages: surveysRes.total_pages || 1 }));
            setStats(statsRes);
            setDepartments(deptsRes);
        } catch (err) {
            if (err.message === 'Session expired') onLogout?.();
            else setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [filter, debouncedSearch, pagination.page, pagination.pageSize, sortBy, sortOrder, onLogout]);

    useEffect(() => { loadData(); }, [loadData]);

    /* ── Create survey ── */
    const handleJdFileChange = (e) => {
        const file = e.target.files[0];
        setJdFileError('');
        if (!file) { setJdFile(null); return; }
        const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md', '.rtf'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(ext)) { setJdFileError('Please upload a PDF, Word document, or text file.'); setJdFile(null); return; }
        if (file.size > 5 * 1024 * 1024) { setJdFileError('File size must be less than 5MB.'); setJdFile(null); return; }
        setJdFile(file);
    };

    const handleCreateSurvey = async (e) => {
        e.preventDefault();
        try {
            let response;
            if (jdInputMode === 'file' && jdFile) {
                const formData = new FormData();
                formData.append('jd_file', jdFile);
                formData.append('employee_name', newSurvey.employee_name);
                formData.append('employee_email', newSurvey.employee_email);
                formData.append('employee_id', newSurvey.employee_id || '');
                formData.append('role_title', newSurvey.role_title);
                formData.append('department', newSurvey.department);
                formData.append('send_email', newSurvey.send_email);
                formData.append('created_by', newSurvey.created_by || adminUser?.username || 'People Operations');
                const token = localStorage.getItem('adminToken');
                const swaPrincipal = localStorage.getItem('swaPrincipal');
                const fileHeaders = {
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    ...(swaPrincipal ? { 'X-MS-CLIENT-PRINCIPAL': swaPrincipal } : {}),
                };
                response = await fetch(`${API_BASE_URL}/api/admin/surveys/with-file`, { method: 'POST', headers: fileHeaders, body: formData });
            } else {
                const surveyData = { employee_name: newSurvey.employee_name, employee_email: newSurvey.employee_email, role_title: newSurvey.role_title, department: newSurvey.department, send_email: newSurvey.send_email, created_by: newSurvey.created_by || adminUser?.username || 'People Operations' };
                if (newSurvey.employee_id) surveyData.employee_id = newSurvey.employee_id;
                if (newSurvey.job_description) surveyData.job_description = newSurvey.job_description;
                response = await fetch(`${API_BASE_URL}/api/admin/surveys`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(surveyData) });
            }
            if (response.status === 401) { onLogout?.(); return; }
            if (!response.ok) { const ed = await response.json().catch(() => ({})); throw new Error(ed.detail || 'Failed to create survey'); }
            const result = await response.json();
            setNewSurvey({ employee_name: '', employee_email: '', employee_id: '', role_title: '', department: '', job_description: '', created_by: adminUser?.username || 'People Operations', send_email: true });
            setJdFile(null); setJdInputMode('text'); setShowCreateForm(false);
            loadData();
            if (result.email_sent) alert(`Survey created!\n\nEmail sent to ${result.employee_email}\n\nLink: ${result.survey_url}`);
            else if (result.email_error) alert(`Survey created!\n\nNote: Email failed: ${result.email_error}\n\nLink: ${result.survey_url}`);
            else alert(`Survey created!\n\nLink: ${result.survey_url}\n\nCopy this link and send it to ${result.employee_name}`);
        } catch (err) { setError(err.message); }
    };

    /* ── Row actions ── */
    const copyLink = (token) => { const link = `${window.location.origin}/survey/${token}`; navigator.clipboard.writeText(link); setCopiedLink(token); setTimeout(() => setCopiedLink(null), 2000); };

    const deleteSurvey = async (sessionId) => {
        if (!window.confirm('Delete this survey? This cannot be undone.')) return;
        try { const r = await fetch(`${API_BASE_URL}/api/admin/sessions/${sessionId}`, { method: 'DELETE', headers: getAuthHeaders() }); if (r.status === 401) { onLogout?.(); return; } loadData(); } catch { setError('Failed to delete survey'); }
    };

    const toggleSurveyClosed = async (sessionId, currentIsClosed) => {
        const action = currentIsClosed ? 'reopen' : 'close';
        if (!window.confirm(currentIsClosed ? 'Reopen this survey?' : 'Close this survey?')) return;
        setTogglingClosed(sessionId);
        try { const r = await fetch(`${API_BASE_URL}/api/admin/sessions/${sessionId}/toggle-closed`, { method: 'POST', headers: getAuthHeaders() }); if (r.status === 401) { onLogout?.(); return; } if (!r.ok) { const d = await r.json(); throw new Error(d.detail || `Failed to ${action}`); } loadData(); } catch (err) { setError(`Failed to ${action}: ${err.message}`); } finally { setTogglingClosed(null); }
    };

    const sendEmail = async (sessionId, status) => {
        if (status === 'completed') { alert('Cannot resend: survey completed.'); return; }
        setSendingEmail(sessionId);
        try { const r = await fetch(`${API_BASE_URL}/api/admin/surveys/send-email`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ session_id: sessionId }) }); if (r.status === 401) { onLogout?.(); return; } const result = await r.json(); if (!r.ok) { alert(result.detail || 'Failed to send email'); return; } if (result.success) alert(`✓ Email sent to ${result.employee_email}`); else alert(`Failed: ${result.message}`); } catch { alert('Network error sending email.'); } finally { setSendingEmail(null); }
    };

    const sendPulse = async (sessionId, employeeName) => {
        setSendingPulse(sessionId);
        try {
            const r = await fetch(`${API_BASE_URL}/api/admin/pulse/send?session_id=${encodeURIComponent(sessionId)}`, {
                method: 'POST',
                headers: getAuthHeaders(),
            });
            if (r.status === 401) { onLogout?.(); return; }
            const result = await r.json();
            if (!r.ok) { alert(result.detail || 'Failed to send pulse survey'); return; }
            const copied = await navigator.clipboard.writeText(result.pulse_url).then(() => true).catch(() => false);
            alert(`✓ Pulse survey created for ${employeeName}!\n\n${copied ? 'Link copied to clipboard:\n' : 'Share this link:\n'}${result.pulse_url}`);
        } catch { alert('Network error creating pulse survey.'); } finally { setSendingPulse(null); }
    };

    const openEditModal = (survey) => {
        setEditForm({
            employee_name: survey.employee_name || '',
            employee_email: survey.employee_email || '',
            employee_id: survey.employee_id || '',
            role_title: survey.role_title || '',
            department: survey.department || '',
        });
        setEditError('');
        setEditingSurvey(survey);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        setEditSaving(true);
        setEditError('');
        try {
            const payload = { ...editForm };
            // Only send employee_id if it has a value (it's optional)
            if (!payload.employee_id) delete payload.employee_id;
            const r = await fetch(`${API_BASE_URL}/api/admin/sessions/${editingSurvey.id}`, {
                method: 'PATCH',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload),
            });
            if (r.status === 401) { onLogout?.(); return; }
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d.detail || 'Failed to update');
            }
            setEditingSurvey(null);
            loadData();
        } catch (err) {
            setEditError(err.message);
        } finally {
            setEditSaving(false);
        }
    };

    const formatDate = (ds) => { if (!ds) return '-'; return new Date(ds).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
    const getStatusClass = (s) => { switch (s) { case 'completed': return 'ds-badge-success'; case 'in_progress': return 'ds-badge-warning'; default: return 'ds-badge-neutral'; } };

    const generatePageNumbers = () => {
        const pages = []; const total = pagination.totalPages; const current = pagination.page;
        if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); }
        else if (current <= 3) pages.push(1, 2, 3, 4, '...', total);
        else if (current >= total - 2) pages.push(1, '...', total - 3, total - 2, total - 1, total);
        else pages.push(1, '...', current - 1, current, current + 1, '...', total);
        return pages;
    };

    /* ── Render ── */
    return (
        <div>
            {/* Header */}
            <div className="ds-page-header">
                <div className="ds-page-header-top">
                    <div>
                        <h1 className="ds-page-title">Work Studies</h1>
                        <p className="ds-page-subtitle">Manage and monitor all employee work studies.</p>
                    </div>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div style={{ background: 'var(--color-error-light)', color: '#CC2F26', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <AlertCircle size={18} />
                        <span>{error}</span>
                    </div>
                    <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: '18px' }}>×</button>
                </div>
            )}

            {/* Toolbar */}
            <div className="ds-toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <button className="ds-btn ds-btn-primary" onClick={() => setShowCreateForm(true)}>
                        <Plus size={18} />
                        New Work Study
                    </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div className="ds-search-input">
                        <Search size={16} className="ds-search-icon" />
                        <input type="text" placeholder="Search name, email, role, dept..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        {searchTerm && <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: '14px', padding: '2px 6px' }}>×</button>}
                    </div>
                    <select className="ds-filter-select" value={filter.status} onChange={(e) => { setFilter(f => ({...f, status: e.target.value})); setPagination(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Statuses</option>
                        <option value="not_started">Not Started</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                    </select>
                    <select className="ds-filter-select" value={filter.department} onChange={(e) => { setFilter(f => ({...f, department: e.target.value})); setPagination(p => ({ ...p, page: 1 })); }}>
                        <option value="">All Departments</option>
                        {departments.map(dept => (<option key={dept} value={dept}>{dept}</option>))}
                    </select>
                    <select className="ds-filter-select" value={`${sortBy}-${sortOrder}`} onChange={(e) => { const [f, o] = e.target.value.split('-'); setSortBy(f); setSortOrder(o); }}>
                        <option value="created_at-desc">Newest First</option>
                        <option value="created_at-asc">Oldest First</option>
                        <option value="employee_name-asc">Name A-Z</option>
                        <option value="employee_name-desc">Name Z-A</option>
                        <option value="department-asc">Department A-Z</option>
                        <option value="status-asc">Status</option>
                    </select>
                </div>
            </div>

            {/* Results count */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-2) 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                {debouncedSearch && <span>Found {pagination.total} results for "{debouncedSearch}"</span>}
                <span style={{ marginLeft: 'auto' }}>Showing {surveys.length > 0 ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0}–{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}</span>
            </div>

            {/* Loading */}
            {isLoading && !surveys.length && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-10)' }}>
                    <div className="dash-loading-spinner" />
                    <p>Loading surveys...</p>
                </div>
            )}

            {/* Table */}
            <div className="ds-card">
                {surveys.length === 0 && !isLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-10)', color: 'var(--color-text-tertiary)' }}>
                        <Briefcase size={48} style={{ opacity: 0.3 }} />
                        {debouncedSearch ? <p>No surveys matching "{debouncedSearch}".</p> : <p>No surveys yet. Create your first work study.</p>}
                    </div>
                ) : (
                    <>
                        <table className="ds-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Department</th>
                                    <th>Status</th>
                                    <th>Progress</th>
                                    <th>Created</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {surveys.map(survey => (
                                    <tr key={survey.id}>
                                        <td>
                                            <div className="ds-cell-with-avatar">
                                                <div className="ds-avatar">{(survey.employee_name || '?').charAt(0).toUpperCase()}</div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <strong>{survey.employee_name}</strong>
                                                    <span>{survey.role_title}</span>
                                                    {survey.employee_email && <span className="ds-cell-email">{survey.employee_email}</span>}
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="ds-badge ds-badge-info">{survey.department}</span></td>
                                        <td>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <span className={`ds-badge ${getStatusClass(survey.status)}`}>{survey.status.replace('_', ' ')}</span>
                                                {survey.is_closed && <span className="ds-badge ds-badge-error" title="Survey closed by admin">Closed</span>}
                                            </div>
                                        </td>
                                        <td>
                                            {survey.progress ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                    <div className="ds-progress"><div className={`ds-progress-fill ${survey.progress.percent_complete >= 100 ? 'green' : 'blue'}`} style={{ width: `${survey.progress.percent_complete}%` }} /></div>
                                                    <span style={{ fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap' }}>{Math.round(survey.progress.percent_complete)}%</span>
                                                </div>
                                            ) : <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>}
                                        </td>
                                        <td><span style={{ fontSize: 'var(--font-size-sm)' }}>{formatDate(survey.created_at)}</span></td>
                                        <td>
                                            <div className="ds-action-row">
                                                <button className="ds-action-btn" onClick={() => setViewingResponses(survey.id)} title="View responses">
                                                    <Eye size={15} />
                                                </button>
                                                <button className="ds-action-btn" onClick={() => openEditModal(survey)} title="Edit details">
                                                    <Pencil size={15} />
                                                </button>
                                                {survey.status === 'completed' && (
                                                    <button className="ds-action-btn" onClick={() => setViewingReport(survey)} title="View AI report">
                                                        <FileText size={15} />
                                                    </button>
                                                )}
                                                {survey.status === 'completed' && (
                                                    <button className="ds-action-btn" onClick={() => sendPulse(survey.id, survey.employee_name)} disabled={sendingPulse === survey.id} title="Send pulse survey">
                                                        {sendingPulse === survey.id ? <span className="dash-act-spinner" /> : <MessageSquare size={15} />}
                                                    </button>
                                                )}
                                                <button className="ds-action-btn" onClick={() => sendEmail(survey.id, survey.status)} disabled={sendingEmail === survey.id || survey.status === 'completed'} title={survey.status === 'completed' ? 'Completed' : 'Send email'}>
                                                    {sendingEmail === survey.id ? <span className="dash-act-spinner" /> : <Mail size={15} />}
                                                </button>
                                                <button className="ds-action-btn" onClick={() => copyLink(survey.token)} title="Copy link">
                                                    {copiedLink === survey.token ? <Check size={15} style={{ color: '#34C759' }} /> : <Link2 size={15} />}
                                                </button>
                                                {survey.status !== 'completed' && (
                                                    <button className="ds-action-btn" onClick={() => toggleSurveyClosed(survey.id, survey.is_closed)} disabled={togglingClosed === survey.id} title={survey.is_closed ? 'Reopen' : 'Close'}>
                                                        {togglingClosed === survey.id ? <span className="dash-act-spinner" /> : survey.is_closed ? <Unlock size={15} /> : <Lock size={15} />}
                                                    </button>
                                                )}
                                                <button className="ds-action-btn destructive" onClick={() => deleteSurvey(survey.id)} title="Delete">
                                                    <Trash2 size={15} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-2)', padding: 'var(--space-4)' }}>
                                <button className="ds-btn ds-btn-secondary" onClick={() => setPagination(p => ({ ...p, page: 1 }))} disabled={pagination.page === 1}>
                                    <ChevronsLeft size={14} />
                                </button>
                                <button className="ds-btn ds-btn-secondary" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                                    <ChevronLeft size={14} />
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                                    {generatePageNumbers().map((pn, i) =>
                                        pn === '...' ? <span key={`e-${i}`} style={{ padding: '0 var(--space-2)' }}>...</span> : <button key={pn} className={`ds-btn ${pagination.page === pn ? 'ds-btn-primary' : 'ds-btn-secondary'}`} onClick={() => setPagination(p => ({ ...p, page: pn }))}>{pn}</button>
                                    )}
                                </div>
                                <button className="ds-btn ds-btn-secondary" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                                    <ChevronRight size={14} />
                                </button>
                                <button className="ds-btn ds-btn-secondary" onClick={() => setPagination(p => ({ ...p, page: p.totalPages }))} disabled={pagination.page === pagination.totalPages}>
                                    <ChevronsRight size={14} />
                                </button>
                                <select className="ds-filter-select" value={pagination.pageSize} onChange={(e) => setPagination(p => ({ ...p, pageSize: parseInt(e.target.value), page: 1 }))}>
                                    <option value="10">10 / page</option>
                                    <option value="25">25 / page</option>
                                    <option value="50">50 / page</option>
                                    <option value="100">100 / page</option>
                                </select>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Create Survey Modal ── */}
            {showCreateForm && (
                <div className="modal-overlay" onClick={() => setShowCreateForm(false)}>
                    <div className="ds-card" style={{ position: 'relative', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto', margin: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="ds-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2>Create New Work Study</h2>
                            <button onClick={() => setShowCreateForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><X size={20} /></button>
                        </div>
                        <form onSubmit={handleCreateSurvey} style={{ padding: 'var(--space-4) var(--space-6)' }}>
                            <div className="form-row">
                                <div className="form-group"><label>Employee Name *</label><input type="text" value={newSurvey.employee_name} onChange={e => setNewSurvey(s => ({...s, employee_name: e.target.value}))} placeholder="John Smith" required /></div>
                                <div className="form-group"><label>Email *</label><input type="email" value={newSurvey.employee_email} onChange={e => setNewSurvey(s => ({...s, employee_email: e.target.value}))} placeholder="john.smith@company.com" required /></div>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Employee ID</label><input type="text" value={newSurvey.employee_id} onChange={e => setNewSurvey(s => ({...s, employee_id: e.target.value}))} placeholder="EMP001" /></div>
                                <div className="form-group"><label>Role/Title *</label><input type="text" value={newSurvey.role_title} onChange={e => setNewSurvey(s => ({...s, role_title: e.target.value}))} placeholder="Senior Marketing Manager" required /></div>
                            </div>
                            <div className="form-group"><label>Department *</label><input type="text" value={newSurvey.department} onChange={e => setNewSurvey(s => ({...s, department: e.target.value}))} placeholder="Marketing" required /></div>
                            <div className="form-group">
                                <label>Job Description (optional)</label>
                                <div className="jd-input-toggle">
                                    <button type="button" className={`ds-btn ${jdInputMode === 'text' ? 'ds-btn-primary' : 'ds-btn-secondary'}`} onClick={() => setJdInputMode('text')}><Type size={14} /> Paste Text</button>
                                    <button type="button" className={`ds-btn ${jdInputMode === 'file' ? 'ds-btn-primary' : 'ds-btn-secondary'}`} onClick={() => setJdInputMode('file')}><FileUp size={14} /> Upload File</button>
                                </div>
                                {jdInputMode === 'text' ? (
                                    <textarea value={newSurvey.job_description} onChange={e => setNewSurvey(s => ({...s, job_description: e.target.value}))} placeholder="Paste the employee's job description here..." rows={4} />
                                ) : (
                                    <div className="file-upload-area">
                                        <input type="file" id="jd-file-input" accept=".pdf,.doc,.docx,.txt,.md,.rtf" onChange={handleJdFileChange} style={{ display: 'none' }} />
                                        <label htmlFor="jd-file-input" className={`file-drop-zone ${jdFile ? 'has-file' : ''}`}>
                                            {jdFile ? (
                                                <div className="file-selected"><FileText size={16} /><span className="file-name">{jdFile.name}</span><button type="button" className="remove-file" onClick={(e) => { e.preventDefault(); setJdFile(null); }}>×</button></div>
                                            ) : (
                                                <div className="upload-prompt"><FileUp size={24} /><span>Click to upload job description</span><small>PDF, Word, or text file (max 5MB)</small></div>
                                            )}
                                        </label>
                                        {jdFileError && <div className="file-error">{jdFileError}</div>}
                                    </div>
                                )}
                            </div>
                            <div className="send-email-option">
                                <input type="checkbox" id="send_email_sv" checked={newSurvey.send_email} onChange={e => setNewSurvey(s => ({...s, send_email: e.target.checked}))} />
                                <label htmlFor="send_email_sv">Send work study invitation email<small>Employee will receive an email with their unique work study link</small></label>
                            </div>
                            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', padding: 'var(--space-4) 0' }}>
                                <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
                                <button type="submit" className="ds-btn ds-btn-primary">{newSurvey.send_email ? 'Create & Send Email' : 'Create Work Study'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Response Viewer */}
            {viewingResponses && <ResponseViewer sessionId={viewingResponses} onClose={() => setViewingResponses(null)} />}

            {/* Edit Employee Details Modal */}
            {editingSurvey && (
                <div className="modal-overlay" onClick={() => setEditingSurvey(null)}>
                    <div className="ds-card" style={{ position: 'relative', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto', margin: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="ds-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2>Edit Employee Details</h2>
                            <button onClick={() => setEditingSurvey(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)' }}><X size={20} /></button>
                        </div>
                        {editError && (
                            <div style={{ background: 'var(--color-error-light)', color: '#CC2F26', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: '0 var(--space-6)' }}>
                                <AlertCircle size={18} />
                                <span>{editError}</span>
                            </div>
                        )}
                        <form onSubmit={handleEditSave} style={{ padding: 'var(--space-4) var(--space-6)' }}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Employee Name *</label>
                                    <input type="text" value={editForm.employee_name} onChange={e => setEditForm(f => ({...f, employee_name: e.target.value}))} placeholder="John Smith" required />
                                </div>
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input type="email" value={editForm.employee_email} onChange={e => setEditForm(f => ({...f, employee_email: e.target.value}))} placeholder="john.smith@company.com" required />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Employee ID</label>
                                    <input type="text" value={editForm.employee_id} onChange={e => setEditForm(f => ({...f, employee_id: e.target.value}))} placeholder="EMP001" />
                                </div>
                                <div className="form-group">
                                    <label>Role/Title *</label>
                                    <input type="text" value={editForm.role_title} onChange={e => setEditForm(f => ({...f, role_title: e.target.value}))} placeholder="Senior Marketing Manager" required />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Department *</label>
                                <input type="text" value={editForm.department} onChange={e => setEditForm(f => ({...f, department: e.target.value}))} placeholder="Marketing" required />
                            </div>
                            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', padding: 'var(--space-4) 0' }}>
                                <button type="button" className="ds-btn ds-btn-secondary" onClick={() => setEditingSurvey(null)}>Cancel</button>
                                <button type="submit" className="ds-btn ds-btn-primary" disabled={editSaving}>
                                    {editSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Report Viewer */}
            {viewingReport && <ReportViewer sessionId={viewingReport.id} session={viewingReport} onClose={() => setViewingReport(null)} />}
        </div>
    );
}

export default SurveysList;
